import { usePage } from "@inertiajs/react"

/**
 * Hook for accessing sidebar permissions computed by the backend.
 *
 * Permissions are computed server-side in SidebarPermissions service
 * based on Pundit policies and shared via Inertia props.
 *
 * @returns {Object} Permission helpers
 * @property {Function} canViewSidebarItem - Check if sidebar item is visible by permission key
 * @property {boolean} isOrgAdmin - Current user is org admin for current organization
 * @property {boolean} isMasterAdmin - Current user is master admin
 * @property {boolean} hasOrganization - Organization context is present
 * @property {Object} sidebarItems - Raw sidebar item permissions object
 */
export function usePermissions() {
  const { props } = usePage()
  const permissions = props.sidebar_permissions || {}
  const items = permissions.items || {}
  const context = permissions.context || {}

  return {
    // Check if a specific sidebar item is accessible
    canViewSidebarItem: (permissionKey) => {
      if (!permissionKey) return true // No permission required (e.g., external links)
      return items[permissionKey] === true
    },

    // Context flags for convenience
    isOrgAdmin: context.is_org_admin || false,
    isMasterAdmin: context.is_master_admin || false,
    hasOrganization: context.has_organization || false,

    // Raw access for advanced use cases
    sidebarItems: items,
    context: context
  }
}

/**
 * Hook for page-level permissions passed via controller props.
 * Use this for action-specific permissions like can_create, can_update.
 *
 * @returns {Object} Page permissions object from controller props
 */
export function usePagePermissions() {
  const { props } = usePage()
  return props.permissions || {}
}
